/* ============================================================================
   Enshield public design system — SHELL
   ----------------------------------------------------------------------------
   The one header and one footer wrapped around every public route.

   Route-dependent behaviour is deliberately limited to destinations and
   states: where "Pricing" points, whether "Sign in" is shown, which nav item
   is current. Geometry — header height, padding, logo size, nav baseline,
   button sizing, container width — is identical everywhere and is not
   parameterised at all, so it cannot drift back apart.
   ========================================================================== */
import { RevealGroup, RevealItem, ScrollProgress } from '../components/motion';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { BrandMark } from '../components/Brand';
import { Container, Btn } from './primitives';

/** The only genuinely per-route variation the shell needs. */
function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [stuck, setStuck] = useState(false);
  const reducedMotion = useReducedMotion();
  const location = useLocation();
  const menuRef = useRef<HTMLButtonElement>(null);
  const restoreDestinationFocus = useRef(false);

  const activateDestination = (event: MouseEvent<HTMLElement>) => {
    if (!open || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;
    const destination = new URL(link.href);
    if (destination.origin !== window.location.origin) return;
    restoreDestinationFocus.current = destination.pathname === location.pathname;
    setOpen(false);
  };

  // Route focus/scroll belongs to the incoming page, after its mount.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // The header only earns its surface once the page has actually moved.
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // While the mobile panel is open: lock the page behind it, and let Escape
  // dismiss it with focus returned to the trigger that opened it.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const background = [...document.querySelectorAll<HTMLElement>('#main-content, .site-foot, .site-head__brand, .skip-link')];
    const previousInert = background.map(element => element.inert);
    background.forEach(element => { element.inert = true; });
    const trigger = menuRef.current;
    const navigation = document.getElementById('site-nav');
    const desktop = window.matchMedia('(min-width: 64em)');
    // CSS visibility can still be hidden in the opening effect. Hand focus
    // over once the open style is rendered, without waiting out the animation.
    let focusFrame = 0;
    const focusFirstLink = () => {
      if (desktop.matches || !navigation) return;
      if (getComputedStyle(navigation).visibility === 'hidden') {
        focusFrame = requestAnimationFrame(focusFirstLink);
        return;
      }
      navigation.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true });
    };
    focusFrame = requestAnimationFrame(focusFirstLink);
    const releaseOnDesktop = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener('change', releaseOnDesktop);
    releaseOnDesktop();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        trigger?.focus({ preventScroll: true });
      }
      if (event.key === 'Tab') {
        const controls = [trigger, ...navigation?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? []].filter((element): element is HTMLElement => !!element && element.getClientRects().length > 0);
        const index = controls.indexOf(document.activeElement as HTMLElement);
        event.preventDefault();
        controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length]?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      background.forEach((element, index) => { element.inert = previousInert[index]; });
      document.removeEventListener('keydown', onKey);
      desktop.removeEventListener('change', releaseOnDesktop);
      if (restoreDestinationFocus.current) {
        restoreDestinationFocus.current = false;
        trigger?.focus({ preventScroll: true });
      }
    };
  }, [open]);

  const navLinks = (
    <>
      <NavLink to="/how-it-works">How it works</NavLink>
      <NavLink to="/faq">FAQ</NavLink>
      <NavLink to="/claims-center">Claims Center</NavLink>
    </>
  );

  return (
    <header className="site-head" data-stuck={stuck}>
      <Container className="site-head__inner">
        <Link className="site-head__brand" to="/" aria-label="Enshield home">
          <BrandMark />
        </Link>

        <button
          ref={menuRef}
          type="button"
          className="site-head__menu"
          aria-expanded={open}
          aria-controls="site-nav"
          onClick={() => setOpen((value) => !value)}
        >
          <span className="sr-only">{open ? 'Close' : 'Open'} navigation</span>
          <span aria-hidden="true"><i /><i /></span>
        </button>

        <motion.nav id="site-nav" className="site-nav" data-open={open} data-motion="mobile-navigation" aria-label="Primary" onClickCapture={activateDestination} animate={open ? 'open' : 'closed'} variants={{ closed: { opacity: 1 }, open: { opacity: 1, transition: { staggerChildren: reducedMotion ? 0 : .045 } } }}>
          {navLinks}
          {/* Duplicated inside the mobile panel because the desktop action row
              is hidden there; both resolve to the same destinations. */}
          <div className="site-nav__mobile-actions">
            <Btn to="/book-a-call">Book a Call</Btn>
          </div>
        </motion.nav>

        <AnimatePresence>
          {open ? <motion.button type="button" tabIndex={-1} aria-label="Close navigation backdrop" data-motion="mobile-navigation-backdrop" className="site-nav__backdrop" onClick={() => { setOpen(false); menuRef.current?.focus({ preventScroll: true }); }} initial={reducedMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} exit={reducedMotion ? undefined : { opacity: 0 }} transition={{ duration: reducedMotion ? 0 : .2 }} /> : null}
        </AnimatePresence>

        <div className="site-head__actions">
          <Btn to="/book-a-call">Book a Call</Btn>
        </div>
      </Container>
    </header>
  );
}

function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-foot">
      <Container>
        <RevealGroup className="site-foot__top">
          <RevealItem className="site-foot__about">
            <BrandMark />
            <p>Managed Package Protection for checkout, claims, and customer support.</p>
          </RevealItem>

          <RevealItem className="site-foot__col">
            <h2>Explore</h2>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/claims-center">Claims Center</Link>
            <Link to="/book-a-call">Book a Call</Link>
          </RevealItem>

          <RevealItem className="site-foot__col">
            <h2>Support</h2>
            <Link to="/learn-more">Learn More</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/track-claim">Track a Claim</Link>
            <Link to="/file-a-claim">File a Claim</Link>
          </RevealItem>
        </RevealGroup>

        {/* Both marketplace submissions require a publicly reachable privacy
            policy and terms link. Both routes returned 200 but were linked
            from nowhere — reachable only by typing the URL. */}
        <div className="site-foot__legal">
          <span>© {year} Enshield, Inc. All rights reserved.</span>
          <span className="u-cluster">
            <Link to="/end-user-policy">End User Policy</Link>
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms and Conditions</Link>
          </span>
        </div>
      </Container>
    </footer>
  );
}

export function SiteShell({ children }: { children: ReactNode }) {
  return (
    <div className="site-shell" data-motion-shell="true">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="site-ambient" data-ambient-orbit="" aria-hidden="true">
        <i className="site-ambient__orbit site-ambient__orbit--one" />
        <i className="site-ambient__orbit site-ambient__orbit--two" />
        <i className="site-ambient__signal" />
      </div>
      <SiteHeader />
      <ScrollProgress />
      <main id="main-content">{children}</main>
      <SiteFooter />
    </div>
  );
}
